<template>
  <v-card flat>
    <v-card-text>
      <div v-if="loading" class="text-center py-6">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!picture" class="grey--text">
        {{ $t("DEALS.VIEW.NO_DOCUMENTS") }}
      </div>
      <div v-else class="chain-scroll">
        <v-alert v-if="stepText && !isRoute" type="info" outlined dense class="mb-4">
          {{ stepText }}
        </v-alert>
        <div
          v-for="(flow, flowIndex) in flows"
          :key="flow.key"
          class="chain-flow"
          :class="{ 'chain-flow--numbered': numbered }"
        >
          <div v-if="numbered" class="chain-flow__index">{{ flowIndex + 1 }}</div>
          <div v-if="flow.route" class="chain-canvas route-canvas">
            <div class="route">
              <div class="route-origin">
                <div class="route-place">
                  <span class="route-place__role">{{ roleLabel(flow.route.origin.role) }}</span>
                  <strong>{{ flow.route.origin.title }}</strong>
                  <span v-if="flow.route.origin.detail" class="route-place__detail">{{ flow.route.origin.detail }}</span>
                  <span v-if="flow.route.origin.quantity" class="route-place__qty">{{ formatQty(flow.route.origin.quantity) }}</span>
                </div>
                <DealTradeChainNode
                  v-for="doc in flow.route.origin.documents"
                  :key="`${doc.kind}-${doc.id}`"
                  :chip="doc"
                />
              </div>

              <div class="route-link">
                <div class="route-link__line"></div>
                <DealTradeChainNode v-if="flow.route.transport" :chip="flow.route.transport" />
                <div v-else class="chain-node chain-node--ghost">
                  {{ $t("DEALS.VIEW.CHAIN_NODE_TO") }}
                </div>
              </div>

              <div class="route-branches">
                <div
                  v-for="(branch, branchIndex) in flow.route.branches"
                  :key="`branch-${flow.key}-${branchIndex}`"
                  class="route-branch"
                >
                  <div class="route-branch__stem"></div>
                  <div class="route-place">
                    <span class="route-place__role">{{ roleLabel(branch.role) }}</span>
                    <strong>{{ branch.title }}</strong>
                    <span v-if="branch.detail" class="route-place__detail">{{ branch.detail }}</span>
                    <span v-if="branch.quantity" class="route-place__qty">{{ formatQty(branch.quantity) }}</span>
                  </div>
                  <DealTradeChainNode
                    v-for="doc in branch.documents"
                    :key="`${doc.kind}-${doc.id}`"
                    :chip="doc"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="chain-canvas">
            <div v-if="flow.board.quantity" class="chain-caption">
              {{ formatQty(flow.board.quantity) }}
              <template v-if="lastParty(flow.board)"> · {{ lastParty(flow.board).title }}</template>
            </div>
            <div
              class="chain-board"
              :class="{ 'chain-board--split': boardDeals(flow.board).length === 2 }"
              :style="boardStyle(flow.board)"
            >
              <template v-if="boardDeals(flow.board).length === 2">
                <div class="chain-side chain-side--seller"></div>
                <div class="chain-side chain-side--buyer"></div>
              </template>

              <div
                v-for="(deal, dealIndex) in boardDeals(flow.board)"
                :key="`deal-${flow.key}-${deal.id}`"
                class="chain-deal"
                :class="dealIndex === 0 ? 'chain-deal--seller' : 'chain-deal--buyer'"
                :style="dealSpan(boardDeals(flow.board), dealIndex, flow.board.parties.length)"
              >
                <span>{{ $t("DEALS.VIEW.CHAIN_DEAL", { code: deal.title }) }}</span>
                <router-link v-if="dealLink(deal)" :to="dealLink(deal)">{{ deal.code }}</router-link>
                <em v-else>—</em>
              </div>

              <div
                v-for="(leg, index) in flow.board.gaps"
                :key="`purchase-${flow.key}-${index}`"
                class="chain-gap chain-gap--top"
                :style="gapStyle(index, 2)"
              >
                <DealTradeChainNode
                  v-for="chip in leg.purchase"
                  :key="`${chip.kind}-${chip.id}`"
                  :chip="chip"
                />
              </div>

              <div
                v-for="(party, index) in flow.board.parties"
                :key="`${flow.key}-${party.key}-${index}`"
                class="chain-party"
                :style="partyStyle(index, 3)"
              >
                {{ party.title }}
              </div>

              <div
                v-for="(leg, index) in flow.board.gaps"
                :key="`link-${flow.key}-${index}`"
                class="chain-links"
                :style="gapStyle(index, 3)"
              >
                <div v-if="leg.purchase.length" class="chain-arrow chain-arrow--left"></div>
                <div v-if="leg.sales.length" class="chain-arrow chain-arrow--right"></div>
              </div>

              <div
                v-for="(leg, index) in flow.board.gaps"
                :key="`sale-${flow.key}-${index}`"
                class="chain-gap chain-gap--bottom"
                :style="gapStyle(index, 4)"
              >
                <DealTradeChainNode
                  v-for="chip in leg.sales"
                  :key="`${chip.kind}-${chip.id}`"
                  :chip="chip"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { GET_DEAL_TRADE_CHAIN } from "@/store/deals.module";
import DealTradeChainNode from "@/views/pages/deals/partials/DealTradeChainNode.vue";

export default {
  name: "DealTradeChain",
  components: { DealTradeChainNode },
  props: {
    dealId: { type: Number, required: true },
  },
  data() {
    return {
      loading: false,
      picture: null,
    };
  },
  computed: {
    isRoute() {
      return this.picture?.layout === "route";
    },
    flows() {
      if (!this.picture) {
        return [];
      }
      if (this.isRoute) {
        return (this.picture.routes || []).map((route, index) => ({
          key: `route-${route.transport?.id || index}`,
          route,
        }));
      }
      const boards = [];
      if (this.picture.parties) {
        boards.push(this.picture);
      }
      (this.picture.chains || []).forEach((board) => boards.push(board));
      const boardFlows = boards.map((board, index) => ({
        key: `board-${index}`,
        board,
        route: null,
      }));
      const routes = (this.picture.movements || []).map((route, index) => ({
        key: `movement-${route.transport?.id || index}`,
        route,
      }));
      return [...boardFlows, ...routes];
    },
    numbered() {
      return this.flows.length > 1;
    },
    stepText() {
      const step = this.picture && this.picture.next_step;
      if (!step) {
        return "";
      }
      const key = `DEALS.VIEW.CHAIN_STEPS.${step.code}`;
      return this.$te(key) ? this.$t(key) : step.message;
    },
  },
  watch: {
    dealId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    gapStyle(index, row) {
      return { gridColumn: index * 2 + 2, gridRow: row };
    },
    partyStyle(index, row) {
      return { gridColumn: index * 2 + 1, gridRow: row };
    },
    lastParty(board) {
      const parties = board?.parties || [];
      return parties[parties.length - 1] || null;
    },
    boardDeals(board) {
      return (board?.parties || []).flatMap((party, index) => {
        if (!party.deal) {
          return [];
        }
        return [{ ...party.deal, title: party.title, partyIndex: index }];
      });
    },
    dealSpan(deals, dealIndex, partyCount) {
      const startParty = dealIndex === 0 ? 0 : deals[dealIndex].partyIndex;
      const endParty = dealIndex === deals.length - 1
        ? partyCount - 1
        : deals[dealIndex + 1].partyIndex - 1;
      return {
        gridColumn: `${startParty * 2 + 1} / ${endParty * 2 + 2}`,
        gridRow: 1,
      };
    },
    boardStyle(board) {
      const count = (board?.parties || []).length || 1;
      const columns = [];
      for (let index = 0; index < count; index += 1) {
        columns.push("156px");
        if (index < count - 1) {
          columns.push("minmax(156px, 1fr)");
        }
      }
      return {
        gridTemplateColumns: columns.join(" "),
        minWidth: `${count * 300}px`,
      };
    },
    dealLink(deal) {
      if (!deal?.can_open || !deal.id) {
        return null;
      }
      return { name: "deals-view", params: { id: deal.id } };
    },
    roleLabel(role) {
      return this.$t(role === "loading" ? "DEALS.VIEW.CHAIN_LOADING" : "DEALS.VIEW.CHAIN_UNLOADING");
    },
    formatQty(value) {
      const number = Number(value);
      if (!number) {
        return "";
      }
      return new Intl.NumberFormat("en-US").format(number).replace(/,/g, " ");
    },
    async load() {
      if (!this.dealId) {
        return;
      }
      this.loading = true;
      try {
        this.picture = await this.$store.dispatch(GET_DEAL_TRADE_CHAIN, this.dealId);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chain-scroll {
  overflow-x: auto;
}

.chain-flow--numbered {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.chain-flow--numbered + .chain-flow--numbered {
  margin-top: 18px;
}

.chain-flow--numbered .chain-canvas {
  flex: 1;
}

.chain-flow__index {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  margin-top: 12px;
  border-radius: 50%;
  background: #1c2430;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
}

.chain-caption {
  margin: 0 12px 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1c2430;
}

.chain-canvas {
  min-width: 640px;
  padding: 12px 8px 8px;
  background-color: #f4f6f8;
  background-image:
    linear-gradient(to right, rgba(55, 68, 82, 0.055) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(55, 68, 82, 0.055) 1px, transparent 1px);
  background-size: 24px 24px;
  border: 1px solid #d5dbe3;
}

.chain-board {
  position: relative;
  display: grid;
  grid-template-rows: auto auto auto auto;
  padding: 4px 12px 8px;
}

.chain-side {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
}

.chain-side--seller {
  left: 0;
  width: calc(50% - 6px);
  background: rgba(25, 118, 210, 0.1);
  border: 1px solid rgba(25, 118, 210, 0.38);
}

.chain-side--buyer {
  right: 0;
  width: calc(50% - 6px);
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.45);
}

.chain-deal {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px 16px;
  font-size: 13px;
  color: #243140;
  z-index: 2;

  a {
    color: inherit;
    font-weight: 600;
  }

  em {
    font-style: normal;
    color: #8b97a6;
  }
}

.chain-deal--seller {
  grid-column: 1 / 4;
  align-items: flex-start;
  color: #1565c0;
}

.chain-deal--buyer {
  grid-column: 5 / 8;
  align-items: flex-start;
  color: #ef6c00;
}

.chain-gap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
  z-index: 2;
}

.chain-gap--top {
  justify-content: flex-end;
  padding-bottom: 10px;
}

.chain-gap--bottom {
  justify-content: flex-start;
  padding-top: 10px;
}

.chain-links {
  position: relative;
  align-self: stretch;
  z-index: 3;
  pointer-events: none;
}

.chain-arrow {
  position: absolute;
  left: -4px;
  right: -4px;
  height: 3px;
  background: #243140;
}

.chain-arrow::before,
.chain-arrow::after {
  content: "";
  position: absolute;
  top: 50%;
}

.chain-arrow::before {
  width: 8px;
  height: 8px;
  background: #243140;
  border-radius: 50%;
  transform: translateY(-50%);
}

.chain-arrow--left {
  top: 18px;
}

.chain-arrow--left::before {
  right: -4px;
}

.chain-arrow--left::after {
  left: 0;
  transform: translate(-100%, -50%);
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-right: 12px solid #243140;
}

.chain-arrow--right {
  bottom: 18px;
}

.chain-arrow--right::before {
  left: -4px;
}

.chain-arrow--right::after {
  right: 0;
  transform: translate(100%, -50%);
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 12px solid #243140;
}

.chain-party {
  justify-self: center;
  align-self: center;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 148px;
  min-height: 76px;
  overflow-wrap: anywhere;
  padding: 12px 14px;
  background: #fff;
  border: 2px solid #1c2430;
  text-align: center;
  font-weight: 600;
  line-height: 1.25;
  color: #1c2430;
}

.chain-transport {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 28px 30px 16px;

  ::v-deep .chain-node,
  .chain-node--ghost {
    position: relative;
    z-index: 2;
    background: #fff;
  }
}

.chain-transport__line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: #243140;
  transform: translateY(-50%);
}

.chain-transport__head {
  position: absolute;
  right: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 9px solid #243140;
}

.chain-node--ghost {
  min-width: 148px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px dashed #8b97a6;
  color: #6b7785;
  text-align: center;
  font-size: 12px;
  position: relative;
  z-index: 2;
}

.route-canvas + .route-canvas {
  margin-top: 16px;
}

.route {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 760px;
}

.route-origin,
.route-link,
.route-branch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.route-origin,
.route-link {
  flex: 0 0 180px;
}

.route-link {
  position: relative;
  padding-top: 22px;
}

.route-link__line {
  position: absolute;
  top: 46px;
  left: 0;
  right: -12px;
  height: 2px;
  background: #243140;
}

.route-link .chain-node,
.route-link .chain-node--ghost {
  position: relative;
  z-index: 2;
  background: #fff;
}

.route-branches {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  margin-top: 46px;
  padding-top: 16px;
  border-top: 2px solid #243140;
}

.route-branch {
  position: relative;
  flex: 1 0 176px;
  max-width: 220px;
  z-index: 1;
}

.route-branch__stem {
  position: absolute;
  top: -16px;
  left: 50%;
  width: 2px;
  height: 16px;
  background: #243140;
  transform: translateX(-50%);
}

.route-place {
  box-sizing: border-box;
  width: 100%;
  max-width: 176px;
  min-height: 76px;
  padding: 10px 12px;
  background: #fff;
  border: 2px solid #1c2430;
  text-align: center;
  color: #1c2430;
}

.route-place__role {
  display: block;
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #5d6b7a;
}

.route-place strong {
  display: block;
  font-size: 14px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.route-place__detail,
.route-place__qty {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.25;
  color: #5d6b7a;
}

.route-place__qty {
  font-weight: 600;
  color: #1c2430;
}
</style>
